import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { shopifyFetch, COLLECTION_BY_HANDLE, PRODUCTS_QUERY, nodes } from "@/lib/shopify";
import { Reveal, Eyebrow } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const SORTS = [
  { label: "Featured", value: "BEST_SELLING", reverse: false },
  { label: "Newest", value: "CREATED_AT", reverse: true },
  { label: "Price: low to high", value: "PRICE", reverse: false },
  { label: "Price: high to low", value: "PRICE", reverse: true },
];

export default function CollectionPage() {
  const { handle } = useParams();
  const isAll = !handle;
  const [sort, setSort] = useState(SORTS[0]);
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    const load = async () => {
      try {
        if (isAll) {
          const d = await shopifyFetch(PRODUCTS_QUERY, {
            first: 48,
            sortKey: sort.value,
            reverse: sort.reverse,
          });
          if (!cancelled) {
            setCollection(null);
            setProducts(nodes(d.products));
            setState("ready");
          }
        } else {
          const d = await shopifyFetch(COLLECTION_BY_HANDLE, {
            handle,
            first: 48,
            sortKey: sort.value === "CREATED_AT" ? "CREATED" : sort.value,
            reverse: sort.reverse,
          });
          if (!cancelled) {
            if (!d.collection) {
              setState("notfound");
            } else {
              setCollection(d.collection);
              setProducts(nodes(d.collection.products));
              setState("ready");
            }
          }
        }
      } catch {
        if (!cancelled) setState("error");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [handle, isAll, sort]);

  const title = isAll ? "The whole weird catalogue" : collection?.title || handle;

  return (
    <main data-testid="collection-page" className="bg-void pt-28 sm:pt-32">
      <Seo
        title={`${isAll ? "Shop All" : collection?.title || "Collection"} — Weird O'Clock`}
        siteName="Weird O'Clock"
        description={
          collection?.description ||
          "Browse the Weird O'Clock collection of quirky clocks and unusual home decor."
        }
      />
      <div className="mx-auto max-w-[1400px] px-5 pb-24 sm:px-8">
        <Reveal>
          <Eyebrow>{isAll ? "Shop all" : "Collection"}</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h1
              data-testid="collection-title"
              className="font-display text-4xl font-black tracking-tight text-ivory sm:text-5xl lg:text-6xl"
            >
              {state === "ready" || state === "loading" ? title : "Collection"}
            </h1>
            <select
              data-testid="collection-sort-select"
              value={SORTS.indexOf(sort)}
              onChange={(e) => setSort(SORTS[parseInt(e.target.value)])}
              className="rounded-full border border-white/15 bg-obsidian px-4 py-2.5 font-mono2 text-xs uppercase tracking-widest text-ivory/80 outline-none focus:border-amber"
            >
              {SORTS.map((s, i) => (
                <option key={s.label} value={i}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          {collection?.description && (
            <p className="mt-4 max-w-2xl text-base text-ivory/50">{collection.description}</p>
          )}
          <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.3em] text-ivory/30">
            {state === "ready" ? `${products.length} objects of desire` : ""}
          </p>
        </Reveal>

        {state === "loading" && (
          <div className="flex items-center gap-3 py-24 text-ivory/40" data-testid="collection-loading">
            <Loader2 className="h-5 w-5 animate-spin text-amber" />
            <span className="font-mono2 text-xs uppercase tracking-[0.25em]">Summoning products…</span>
          </div>
        )}

        {state === "error" && (
          <p
            data-testid="collection-error"
            className="mt-10 rounded-lg border border-amber/30 bg-amber/5 px-5 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-amber"
          >
            Connecting to the Weird O'Clock Shopify store — check back in a tick.
          </p>
        )}

        {state === "notfound" && (
          <div className="py-24" data-testid="collection-not-found">
            <p className="font-editorial text-2xl italic text-ivory/70">
              This collection slipped through a time warp.
            </p>
            <Link
              to="/shop"
              className="mt-4 inline-block font-mono2 text-xs uppercase tracking-[0.2em] text-amber hover:underline"
            >
              Browse everything instead
            </Link>
          </div>
        )}

        {state === "ready" && products.length === 0 && (
          <p className="py-20 font-mono2 text-xs uppercase tracking-[0.2em] text-ivory/40">
            Nothing here yet — new weirdness lands soon.
          </p>
        )}

        <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </main>
  );
}
